import CloseIcon from "@mui/icons-material/Close";
import { IconButton,TextField } from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import { Stack } from "@mui/system";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import AttToken from "../../../helpers/attToken";
import { trackEventMatomo } from "../../../helpers/matomo";
import Manages from "../../../service/Manages";
import queryClient from "../../../service/query";
import { ManageTypeResponse,gerenciaType } from "../../../types/managements/types";
import GenericAutoComplete from "../../GenericAutoComplete";

const managesService = new Manages();

function ChangeManageModal({
  open,
  setOpen,
  result,
  selectedManage,
  setSelectedManage,
}: {
  result: ManageTypeResponse;
  selectedManage: gerenciaType;
  setSelectedManage: Function;
  open: boolean;
  setOpen: Function;
}) {
  const { t } = useTranslation("translation");

  const { mutate, isPending } = useMutation({
    mutationKey: ["EDIT-_MANAGE"],
    mutationFn: async () => {
      const promise = toast.promise(
        async () => {
          const token = await AttToken();
          if (token) {
            const body = {
              ...token,
              nome: selectedManage.nome,
              clientes: selectedManage.clientes,
              formularios: selectedManage.formularios,
              gerentes: selectedManage.gerentes,
              equipes: selectedManage.equipes,
              dataDelecao: selectedManage.dataDelecao,
              users: selectedManage.users,
              id: selectedManage.id,
            };
            const res = await managesService.updateManage(body);
            if (!res) throw new Error("Error updating manage");
            return res;
          }
        },
        {
          success: `${t("Managements.change")}`,
          error: `${t("custom.erro_ao_alterar_gerencia")}`,
          pending: `${t("toast.savingChanges")}`,
        },
        { toastId: `edit-manage-${selectedManage.id}` },
      );
      return promise;
    },
    onSuccess: () => {
      queryClient.resetQueries({ queryKey: ["GET_MANAGES"] });
      setOpen(false);
    },
  });

  return (
    <Dialog fullWidth maxWidth="md" open={open} onClose={() => setOpen(false)} scroll={"paper"}>
      <DialogTitle id="scroll-dialog-title"> {t("Managements.registered.edit")}</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={() => setOpen(false)}
        sx={(theme) => ({
          position: "absolute",
          right: 8,
          top: 8,
          color: theme.palette.grey[500],
        })}>
        <CloseIcon />
      </IconButton>
      <DialogContent dividers={true}>
        <Stack direction="column" justifyContent="flex-start" alignItems="flex-start" spacing={2} width="100%">
          <TextField
            fullWidth
            label={t("name")}
            onClick={() => trackEventMatomo("modal gerencia", "click", "input", "edita valor")}
            value={selectedManage && selectedManage.nome}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSelectedManage({ ...selectedManage, nome: e.target.value })}
            disabled={selectedManage && selectedManage.geral}
          />
          <GenericAutoComplete
            label={t("teamManage.modal.customer").replace(":", "")}
            options={
              result?.[1]?.data
                ?.filter((item, index, array) => {
                  return array.indexOf(item) === index;
                })
                ?.filter((e) => e?.status === "ativo")
                ?.map((e) => ({ label: e?.nome, value: e?.id })) || []
            }
            selectedValues={selectedManage.clientes ?? []}
            onChange={(values) => {
              setSelectedManage({ ...selectedManage, clientes: values });
            }}
            disabled={!result[1]?.data?.length || (selectedManage && selectedManage.geral)}
            loading={isPending}
            truncateLength={25}
          />
          <GenericAutoComplete
            label={t("teamManage.modal.form")}
            options={
              result?.[4]?.data
                ?.filter((item, index, array) => {
                  return array.indexOf(item) === index;
                })
                ?.map((e) => ({ label: e.nome, value: e.id })) || []
            }
            selectedValues={selectedManage.formularios ?? []}
            onChange={(values) => {
              setSelectedManage({ ...selectedManage, formularios: values });
            }}
            disabled={!result[4]?.data?.length || (selectedManage && selectedManage.geral)}
            loading={isPending}
            truncateLength={25}
          />
          <GenericAutoComplete
            label={t("teamManage.modal.teams")}
            options={
              result?.[2]?.data
                ?.filter((item, index, array) => {
                  return array.indexOf(item) === index;
                })
                ?.filter((e) => e.status === true)
                ?.map((e) => ({ label: e.nome, value: e.teamID })) || []
            }
            selectedValues={selectedManage.equipes ?? []}
            onChange={(values) => {
              setSelectedManage({ ...selectedManage, equipes: values });
            }}
            disabled={!result[2]?.data?.length || (selectedManage && selectedManage.geral)}
            loading={isPending}
            truncateLength={25}
          />
          <GenericAutoComplete
            label={t("teamManage.modal.manages")}
            options={
              result?.[3]?.data
                ?.filter((item, index, array) => {
                  return array.indexOf(item) === index;
                })
                ?.filter((el) => el?.perfil === "Gerente")
                ?.filter((el) => el.status === "ativo")
                ?.map((e) => ({ label: e.nome, value: e.id })) || []
            }
            selectedValues={selectedManage.gerentes ?? []}
            onChange={(values) => {
              setSelectedManage({ ...selectedManage, gerentes: values });
            }}
            disabled={!result[3]?.data?.length || (selectedManage && selectedManage.geral)}
            loading={isPending}
            truncateLength={25}
          />
          <GenericAutoComplete
            label={t("teamManage.modal.users")}
            options={
              result?.[3]?.data
                ?.filter((item, index, array) => {
                  return array.indexOf(item) === index;
                })
                ?.filter((el) => el.status === "ativo")
                ?.map((e) => ({ label: e.nome, value: e.id })) || []
            }
            selectedValues={selectedManage.users ?? []}
            onChange={(values) => {
              setSelectedManage({ ...selectedManage, users: values });
            }}
            disabled={!result[3]?.data?.length || (selectedManage && selectedManage.geral)}
            loading={isPending}
            truncateLength={25}
          />
          <FormControl fullWidth>
            <InputLabel>{t("status")}</InputLabel>
            <Select
              onChange={(e) => {
                if (e.target.value === "ativo") {
                  setSelectedManage({ ...selectedManage, dataDelecao: 1 });
                } else {
                  setSelectedManage({ ...selectedManage, dataDelecao: Date.now() });
                }
              }}
              value={selectedManage?.dataDelecao && Number(selectedManage?.dataDelecao) !== 1 ? "inativo" : "ativo"}
              label={t("status")}>
              <MenuItem value={"ativo"}>{t("active")}</MenuItem>
              <MenuItem value={"inativo"}>{t("inactive")}</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          disabled={(selectedManage && selectedManage.geral) || isPending}
          onClick={() => {
            trackEventMatomo("modal gerencia", "click", "button", "salva");
            mutate();
          }}>
          {t("save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ChangeManageModal;
